import { ComponentChildren } from "preact"
import { useEffect, useRef, useState } from "preact/hooks"
import { Link, useLocation } from "wouter-preact"
import { is_page_hidden } from "@/app/page_access"
import { UserState } from "@/auth/user"
import { useOrgSettings } from "@/org_settings/state"
import { classes } from "@/utils"
import { DemoResetBanner } from "./demo_reset_banner"

interface Tab {
  name: string
  href: string
}

const tabs: Tab[] = [
  { name: "Connections", href: "/settings/connections" },
  { name: "Users", href: "/settings/users" },
  { name: "Accounting", href: "/settings/accounting" },
  { name: "Categories", href: "/settings/categories" },
  { name: "Coding rules", href: "/settings/coding-rules" },
  { name: "Organization", href: "/settings/organization" },
  { name: "API Keys", href: "/settings/api-keys" },
  { name: "BigWiz", href: "/settings/agent" },
  { name: "Preferences", href: "/settings/preferences" },
]

interface SettingsLayoutProps {
  children: ComponentChildren
  action?: ComponentChildren
}

interface SectionLink {
  id: string
  title: string
}

/** Turn a section title into a stable, URL-safe anchor id. */
export function sectionSlug(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export function SettingsLayout({ children, action }: SettingsLayoutProps) {
  const [location] = useLocation()
  const user = UserState.use()
  const org_settings = useOrgSettings()
  const visible_tabs = tabs.filter((tab: Tab) => !is_page_hidden(tab.href, user, org_settings))
  const contentRef = useRef<HTMLDivElement>(null)
  const [sections, setSections] = useState<SectionLink[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  // Discover navigable sections by scanning the rendered section headings. Re-scan on
  // DOM mutations so conditionally/async-rendered sections (e.g. SWR-loaded) show up too.
  useEffect(() => {
    const container = contentRef.current
    if (!container) return

    function scan() {
      const headings = Array.from(container!.querySelectorAll<HTMLHeadingElement>("h2"))
      const seen = new Map<string, number>()
      const found: SectionLink[] = headings.map(heading => {
        const title = heading.textContent?.trim() || "Section"
        const base = sectionSlug(title) || "section"
        const count = seen.get(base) ?? 0
        seen.set(base, count + 1)
        const id = count === 0 ? base : `${base}-${count}`
        heading.id = id
        // Offset so the heading isn't hidden under the sticky app header when scrolled to.
        heading.style.scrollMarginTop = "5rem"
        return { id, title }
      })
      setSections(prev =>
        prev.length === found.length && prev.every((p, i) => p.id === found[i].id && p.title === found[i].title)
          ? prev
          : found
      )
    }

    scan()
    const observer = new MutationObserver(scan)
    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [location])

  // Highlight the section currently in view.
  useEffect(() => {
    if (sections.length === 0) return
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: "-80px 0px -70% 0px" }
    )
    for (const section of sections) {
      const el = document.getElementById(section.id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [sections])

  function jumpTo(e: Event, id: string) {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
      setActiveId(id)
    }
  }

  return (
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-8 mt-2">
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      <DemoResetBanner className="mb-6" />

      {/* Tab Navigation */}
      <div class="border-b border-gray-200 flex items-center justify-between">
        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
          {visible_tabs.map(tab => {
            const isActive = location === tab.href
            return (
              <Link
                key={tab.href}
                href={tab.href}
                class={classes(
                  isActive
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                  "whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors"
                )}>
                {tab.name}
              </Link>
            )
          })}
        </nav>
        {action}
      </div>

      {/* Content with a sticky in-page section nav */}
      <div class="mt-6 flex gap-8 items-start">
        {sections.length > 1 && (
          <nav class="hidden lg:block sticky top-20 w-48 flex-none" aria-label="Sections">
            <p class="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">On this page</p>
            <ul class="space-y-0.5">
              {sections.map(section => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    onClick={e => jumpTo(e, section.id)}
                    class={classes(
                      "block rounded-md px-3 py-1.5 text-sm transition-colors border-l-2",
                      activeId === section.id
                        ? "border-blue-500 text-blue-600 font-medium bg-blue-50/60"
                        : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}>
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div ref={contentRef} class="flex-1 min-w-0 space-y-10">
          {children}
        </div>
      </div>
    </div>
  )
}

// Inline tests
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest

  it("sectionSlug makes URL-safe anchors", () => {
    expect(sectionSlug("Reason code mapping")).toBe("reason-code-mapping")
    expect(sectionSlug("Auto-code & Simple rules")).toBe("auto-code-simple-rules")
    expect(sectionSlug("  QuickBooks Desktop  ")).toBe("quickbooks-desktop")
  })
}
